interface IPermissionCategory {
  id: string;
  code: string;
  label: string;
  icon: string;
  order: number;
}

interface IPermission {
  id: string;
  code: string;
  description: string;
  scope: 'gym' | 'platform';
  category: IPermissionCategory | null;
}

interface IPermissionGroup {
  code: string;
  label: string;
  icon: string;
  permissions: IPermission[];
}

// Presets são só uma conveniência de UI (pré-marcam caixas) -- não existe
// conceito de "cargo" no backend, só a lista de permissões marcadas.
const EMPLOYEE_PRESET = [
  'view_gym_dashboard',
  'view_gym_users',
  'view_exercises',
  'manage_trainings',
  'manage_measurements',
  'manage_news',
];

const GYM_MANAGER_PRESET = [
  ...EMPLOYEE_PRESET,
  'manage_exercises',
  'manage_gym_users',
  'manage_gym_profile',
  'manage_payments',
  'manage_subscriptions',
];

// Permissões que dão controle sobre outros membros, dados da academia ou
// assinatura/cobrança -- só um detalhe de exibição (tag "SENSÍVEL"), não
// existe isso no catálogo do banco (diferente das categorias).
const SENSITIVE_CODES = new Set([
  'manage_gym_users',
  'manage_gym_profile',
  'manage_subscriptions',
]);

function inferRoleLabel(
  codes: string[],
): 'GESTOR' | 'FUNCIONÁRIO' | 'ALUNO' | 'PERSONALIZADO' {
  const set = new Set(codes.filter(code => code !== 'student'));

  if (set.size === 0) {
    return 'ALUNO';
  }

  if (
    set.size === GYM_MANAGER_PRESET.length &&
    GYM_MANAGER_PRESET.every(code => set.has(code))
  ) {
    return 'GESTOR';
  }

  if (
    set.size === EMPLOYEE_PRESET.length &&
    EMPLOYEE_PRESET.every(code => set.has(code))
  ) {
    return 'FUNCIONÁRIO';
  }

  return 'PERSONALIZADO';
}

const UNCATEGORIZED_GROUP: Omit<IPermissionGroup, 'permissions'> = {
  code: '__UNCATEGORIZED__',
  label: 'Outras',
  icon: 'category',
};

// Agrupa o catálogo pela categoria vinda da API (`permission.category`) --
// nenhuma taxonomia hardcoded aqui. Quem não tiver categoria cai num
// grupo fixo "Outras" no final da lista.
function groupCatalogByCategory(catalog: IPermission[]): IPermissionGroup[] {
  const categorized = new Map<string, IPermissionGroup>();
  const uncategorized: IPermission[] = [];

  catalog.forEach(permission => {
    if (!permission.category) {
      uncategorized.push(permission);
      return;
    }

    const { category } = permission;
    const existing = categorized.get(category.code);

    if (existing) {
      existing.permissions.push(permission);
      return;
    }

    categorized.set(category.code, {
      code: category.code,
      label: category.label,
      icon: category.icon,
      permissions: [permission],
    });
  });

  const orderByCategory = new Map<string, number>();
  catalog.forEach(permission => {
    if (permission.category) {
      orderByCategory.set(permission.category.code, permission.category.order);
    }
  });

  const groups = Array.from(categorized.values()).sort(
    (a, b) =>
      (orderByCategory.get(a.code) ?? 0) - (orderByCategory.get(b.code) ?? 0),
  );

  if (uncategorized.length) {
    groups.push({ ...UNCATEGORIZED_GROUP, permissions: uncategorized });
  }

  return groups;
}

export {
  EMPLOYEE_PRESET,
  GYM_MANAGER_PRESET,
  SENSITIVE_CODES,
  inferRoleLabel,
  groupCatalogByCategory,
};
export type { IPermission, IPermissionCategory, IPermissionGroup };
